/**
 * @fileoverview Customer Base — Branch-wise Customer Distribution page.
 * @description Ported from the InsightBox Banking design system's own
 * `CustomerBase/BranchWiseCustomerDistribution.tsx`. "Aggregate view" (branches compared against
 * each other) covers both the bank-wide `ALL_BRANCHES_ID` scope and a Bank Regional Manager's own
 * region pseudo-id (`isRegionId`) — in the region case, `bankingScope.regionBranchIds` narrows every
 * ranking/sum below to just that region's branches. Selecting any real branch drills into that
 * branch's own trend either way.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  ALL_BRANCHES_ID,
  BranchFilterSelect,
  ComboBarDot,
  FISCAL_YEARS,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  TrendArea,
  branchCustomerGrowth5Y,
  branchCustomers,
  branchLabel,
  core,
  fiveYearGrowth,
  formatNumber,
  formatSignedPct,
  isRegionId,
  monthlyCustomers,
  resolveBankingAccessScope,
  resolveFy,
  topNShare,
  trendLabel,
  useBankingData,
  yoyGrowth,
  zigzagOrder,
} from '@/features/banking'
import SegmentedControl from '@/shared/components/atoms/SegmentedControl'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { ComboBarDotPoint, FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

const RANK_VIEWS = [
  { value: 'all', label: 'All' },
  { value: 'top', label: 'Top 10' },
  { value: 'bottom', label: 'Bottom 10' },
] as const
type RankView = (typeof RANK_VIEWS)[number]['value']

/**
 * @component
 */
function BankingCustomerBasePage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)
  const [rankView, setRankView] = useState<RankView>('all')
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)
  const isAggregateView = branchId === ALL_BRANCHES_ID || isRegionId(branchId)

  const rankViewOptions = RANK_VIEWS.map((v) => ({
    value: v.value,
    label: t(`banking.branchAnalyticsIncomeShare.rankView.${v.value}`),
  }))

  const header = (
    <PageHeader
      kicker={t('banking.customerBase.kicker')}
      title={t('banking.customerBase.title')}
      description={
        isAggregateView && is5Years
          ? t('banking.customerBase.descriptionAggregate5Y')
          : t('banking.customerBase.description')
      }
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data

  // `rows` is the whole network in scope — bank-wide, or (for a Bank Regional Manager) just their
  // own region's branches via `bankingScope.regionBranchIds` — every KPI/summary stat describes it
  // in full; only the chart itself gets sliced down by the Top 10/Bottom 10 toggle below.
  const rows = branchCustomers(data, fy, bankingScope.regionBranchIds)
  const total = rows.reduce((sum, r) => sum + r.count, 0)
  const selected = !isAggregateView ? rows.find((r) => r.branch.id === branchId) : undefined
  const rank = selected ? rows.findIndex((r) => r.branch.id === branchId) + 1 : null

  // "5 Years" ranks branches by FY21->FY25 growth instead of re-showing the FY25 snapshot — Total
  // Customers is a stock (a headcount at each year-end), so summing it across years the way "5Y
  // Sum" sums a flow (income, new members, ...) would double-count the same customers who stuck
  // around; growth is the meaningful multi-year comparison for a stock.
  const growthRows = branchCustomerGrowth5Y(data, bankingScope.regionBranchIds)
  const totalFY21 = branchCustomers(data, 'FY21', bankingScope.regionBranchIds).reduce(
    (sum, r) => sum + r.count,
    0,
  )
  const scopeGrowthPct = ((total - totalFY21) / totalFY21) * 100
  const fastest = growthRows[0]!
  const slowest = growthRows[growthRows.length - 1]!

  // Ranking/KPIs use the sorted rows; the chart uses a zigzagged order so the bars read as an
  // up/down pattern instead of a staircase.
  const rankedGrowthRows =
    rankView === 'top'
      ? growthRows.slice(0, 10)
      : rankView === 'bottom'
        ? growthRows.slice(-10)
        : growthRows
  const rankedRows =
    rankView === 'top' ? rows.slice(0, 10) : rankView === 'bottom' ? rows.slice(-10) : rows
  const chartData: ComboBarDotPoint[] = is5Years
    ? zigzagOrder(rankedGrowthRows).map((r) => ({
        label: r.branch.shortName,
        count: r.netGain,
        sharePct: r.growthPct,
      }))
    : zigzagOrder(rankedRows).map((r) => ({
        label: r.branch.shortName,
        count: r.count,
        sharePct: r.sharePct,
      }))

  // Drilling into one branch makes the aggregate comparison redundant — switch to that branch's
  // own trend. "5 Years" shows the FY21->FY25 trajectory (matching every other single-branch/5-year
  // chart in the app); a single year drills further into that year's own Apr->Mar monthly glide.
  const branchSelector = (f: (typeof FISCAL_YEARS)[number]) =>
    core(data, f, branchId).totalCustomers
  const branchTrend =
    !isAggregateView && is5Years
      ? FISCAL_YEARS.map((f) => ({
          fy: f,
          value: branchSelector(f),
          growthPct: yoyGrowth(f, branchSelector),
        }))
      : null
  const monthly = !isAggregateView && !is5Years ? monthlyCustomers(data, fy, branchId) : null
  const strongestMonth = monthly
    ? monthly.slice(1).reduce((best, m) => (m.growthPct! > best.growthPct! ? m : best))
    : null

  const branchYoy = !isAggregateView
    ? yoyGrowth(fy, (f) => core(data, f, branchId).totalCustomers)
    : null

  const kpis: BankingKpiStripItem[] = isAggregateView
    ? is5Years
      ? [
          {
            label: t('banking.customerBase.kpi.totalCustomersFy25'),
            value: formatNumber(total),
            sublabel: t('banking.customerBase.kpi.acrossBranches', { count: rows.length }),
            highlight: true,
          },
          {
            label: t('banking.customerBase.kpi.fastestGrowing'),
            value: fastest.branch.shortName,
            sublabel: t('banking.customerBase.kpi.growthNetGain', {
              pct: formatSignedPct(fastest.growthPct),
              sign: fastest.netGain >= 0 ? '+' : '',
              count: formatNumber(fastest.netGain),
            }),
            trend: { direction: trendLabel(fastest.growthPct) },
          },
          {
            label: t('banking.customerBase.kpi.bankWideGrowth'),
            value: formatSignedPct(scopeGrowthPct),
            sublabel: t('banking.customerBase.kpi.fromTo', {
              from: formatNumber(totalFY21),
              to: formatNumber(total),
            }),
            trend: { direction: trendLabel(scopeGrowthPct) },
          },
          {
            label: t('banking.customerBase.kpi.slowestGrowing'),
            value: slowest.branch.shortName,
            sublabel: t('banking.customerBase.kpi.growthNetGain', {
              pct: formatSignedPct(slowest.growthPct),
              sign: slowest.netGain >= 0 ? '+' : '',
              count: formatNumber(slowest.netGain),
            }),
            trend: { direction: trendLabel(slowest.growthPct) },
          },
        ]
      : [
          {
            label: t('banking.customerBase.kpi.totalCustomers'),
            value: formatNumber(total),
            sublabel: t('banking.customerBase.kpi.acrossBranches', { count: rows.length }),
            highlight: true,
          },
          {
            label: t('banking.customerBase.kpi.top3Share'),
            value: `${topNShare(data, fy, 3, bankingScope.regionBranchIds).toFixed(2)}%`,
            sublabel: t('banking.customerBase.kpi.ofTotalCustomers'),
          },
          {
            label: t('banking.customerBase.kpi.avgPerBranch'),
            value: formatNumber(total / rows.length),
            sublabel: t('banking.customerBase.kpi.acrossBranches', { count: rows.length }),
          },
          {
            label: t('banking.customerBase.kpi.top5Share'),
            value: `${topNShare(data, fy, 5, bankingScope.regionBranchIds).toFixed(2)}%`,
            sublabel: t('banking.customerBase.kpi.ofTotalCustomers'),
          },
        ]
    : is5Years
      ? [
          {
            label: t('banking.customerBase.kpi.customersFy25'),
            value: formatNumber(branchSelector('FY25')),
            sublabel: branchLabel(branchId),
            highlight: true,
          },
          {
            label: t('banking.customerBase.kpi.customersFy21'),
            value: formatNumber(branchSelector('FY21')),
            sublabel: t('banking.customerBase.kpi.fiveYearsAgo'),
          },
          {
            label: t('banking.customerBase.kpi.fiveYearGrowth'),
            value: formatSignedPct(fiveYearGrowth(branchSelector)),
            sublabel: t('banking.customerBase.kpi.fiveYearRange'),
            trend: { direction: trendLabel(fiveYearGrowth(branchSelector)) },
          },
          {
            label: t('banking.customerBase.kpi.growthRank'),
            value: t('banking.customerBase.kpi.rankOf', {
              rank: growthRows.findIndex((r) => r.branch.id === branchId) + 1,
              total: growthRows.length,
            }),
            sublabel: t('banking.customerBase.kpi.byGrowth'),
          },
        ]
      : [
          {
            label: t('banking.customerBase.kpi.customers'),
            value: formatNumber(selected!.count),
            sublabel: branchLabel(branchId),
            highlight: true,
          },
          {
            label: t('banking.customerBase.kpi.shareOfTotal'),
            value: `${selected!.sharePct.toFixed(2)}%`,
            sublabel: '',
          },
          {
            label: t('banking.customerBase.kpi.branchRank'),
            value: t('banking.customerBase.kpi.rankOf', { rank: rank!, total: rows.length }),
            sublabel: t('banking.customerBase.kpi.byCustomerCount'),
          },
          {
            label: t('banking.customerBase.kpi.yoyGrowth'),
            value: formatSignedPct(branchYoy ?? 0),
            sublabel: t('banking.customerBase.kpi.vsPriorYear'),
            ...(branchYoy !== null && { trend: { direction: trendLabel(branchYoy) } }),
          },
        ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          isAggregateView
            ? is5Years
              ? t('banking.customerBase.chartTitleAggregate5Y')
              : t('banking.customerBase.chartTitleAggregate')
            : is5Years
              ? t('banking.customerBase.chartTitleBranch5Y', { branch: branchLabel(branchId) })
              : t('banking.customerBase.chartTitleBranchMonthly', { branch: branchLabel(branchId) })
        }
        subtitle={
          isAggregateView
            ? is5Years
              ? t('banking.customerBase.chartSubtitleAggregate5Y', {
                  rankSuffix:
                    rankView !== 'all'
                      ? t('banking.customerBase.chartSubtitleRankSuffix', {
                          view: rankView,
                          count: rows.length,
                        })
                      : '',
                })
              : t('banking.customerBase.chartSubtitleAggregateYear', {
                  fy,
                  rankSuffix:
                    rankView !== 'all'
                      ? t('banking.customerBase.chartSubtitleRankSuffix', {
                          view: rankView,
                          count: rows.length,
                        })
                      : '',
                })
            : is5Years
              ? t('banking.customerBase.chartSubtitleBranch5Y')
              : t('banking.customerBase.chartSubtitleBranchMonthly', { fy })
        }
        action={
          isAggregateView && rows.length > 10 ? (
            <SegmentedControl value={rankView} onChange={setRankView} options={rankViewOptions} />
          ) : undefined
        }
      >
        <div className="relative min-h-0 flex-1">
          {isAggregateView ? (
            <ComboBarDot
              data={chartData}
              barName={
                is5Years
                  ? t('banking.customerBase.barNameNetGain')
                  : t('banking.customerBase.barNameCount')
              }
              {...(is5Years && { shareLabel: t('banking.customerBase.shareLabelGrowth') })}
              height="100%"
            />
          ) : (
            // Keyed on the view mode — see TrendArea's own file comment for why.
            <TrendArea
              key={is5Years ? '5y' : 'monthly'}
              data={is5Years ? branchTrend! : monthly!}
              formatValue={formatNumber}
              height="100%"
              colorByTrend
            />
          )}
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={
          is5Years
            ? t('banking.customerBase.summaryTitleFiveYear')
            : t('banking.customerBase.summaryTitleYear', { fy })
        }
        lines={
          isAggregateView
            ? is5Years
              ? [
                  t('banking.customerBase.summary.aggregateFiveYearLine1', {
                    branch: fastest.branch.shortName,
                    pct: formatSignedPct(fastest.growthPct),
                    sign: fastest.netGain >= 0 ? '+' : '',
                    count: formatNumber(fastest.netGain),
                  }),
                  t('banking.customerBase.summary.aggregateFiveYearLine2', {
                    pct: formatSignedPct(scopeGrowthPct),
                    from: formatNumber(totalFY21),
                    to: formatNumber(total),
                    branch: slowest.branch.shortName,
                    slowPct: formatSignedPct(slowest.growthPct),
                  }),
                ]
              : [
                  t('banking.customerBase.summary.aggregateYearLine1', {
                    branch1: rows[0]!.branch.shortName,
                    branch2: rows[1]!.branch.shortName,
                    branch3: rows[2]!.branch.shortName,
                    pct: topNShare(data, fy, 3, bankingScope.regionBranchIds).toFixed(2),
                  }),
                  t('banking.customerBase.summary.aggregateYearLine2', {
                    count: rows.filter((r) => r.sharePct > 8).length,
                  }),
                ]
            : is5Years
              ? [
                  t('banking.customerBase.summary.branchFiveYearLine1', {
                    branch: branchLabel(branchId),
                    fy21: formatNumber(branchSelector('FY21')),
                    fy25: formatNumber(branchSelector('FY25')),
                    pct: formatSignedPct(fiveYearGrowth(branchSelector)),
                  }),
                  t('banking.customerBase.summary.branchFiveYearLine2', {
                    rank: growthRows.findIndex((r) => r.branch.id === branchId) + 1,
                    total: growthRows.length,
                  }),
                ]
              : [
                  t('banking.customerBase.summary.branchYearLine1', {
                    branch: branchLabel(branchId),
                    apr: formatNumber(monthly![0]!.value),
                    mar: formatNumber(monthly![monthly!.length - 1]!.value),
                    fy,
                    net: formatNumber(monthly![monthly!.length - 1]!.value - monthly![0]!.value),
                  }),
                  t('banking.customerBase.summary.branchYearLine2', {
                    month: strongestMonth!.fy,
                    pct: formatSignedPct(strongestMonth!.growthPct!),
                  }),
                ]
        }
      />
    </div>
  )
}

export default BankingCustomerBasePage
